import { faCircleNotch } from "@imaginelearning/mps-icon-library";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "react-bootstrap/Dropdown";
import { AlpService, IAlp, IIlpServiceState, ServiceStatusEnum } from "@imaginelearning/mps-api-service";
import { classMap, UxTabs } from "@imaginelearning/mps-ux-component-library";
import EdgeBackButton from "components/edge-back-button/edge-back-button";
import SmallScreenPanel from "components/small-screen-panel/small-screen-panel";
import useBreakpoint from "../../liftoff/hooks/useBreakpoint";
import React, { useEffect, useState } from "react";
import useGradeRange from "hooks/usegraderange";
import useUser from "hooks/useuser";
import { Subjects } from "../common/enums";
import NoAssignmentPage from "../common/no-assignment-page";
import "./my-assignment-menu.scss";
import MyAssignmentsTile from "./my-assignment-tile";
import utcDateHandler from "../common/date-convertor";
import useHomePage from "../../../hooks/useHomePage";
import useTheme from "../../../hooks/usetheme";

const spinner = (
  <div className="my-assignment-menu-spinner">
    <FontAwesomeIcon size="5x" className="animation spinner d-flex mx-auto" icon={faCircleNotch} />
  </div>
);

function MyAssignmentsMenu() {
  const { theme } = useTheme();
  const { isKto5 } = useGradeRange();
  const [loaded, setLoaded] = useState(false);

  const { isSmallScreen } = useBreakpoint();

  const [alps, setAlps] = useState<IAlp[]>([]);
  const { contentTypeForAlps, user } = useUser();

  useEffect(() => {
    if (user?.id && contentTypeForAlps) {
      const fetchData = async () => {
        const result: IIlpServiceState<IAlp[]> = await AlpService.getAlps(
          user.id,
          user.schoolYear,
          contentTypeForAlps?.type
        );
        if (result.status === ServiceStatusEnum.LOADED && result.response) {
          setAlps(
            (result.response as IAlp[]).map((alp) => {
              if (alp.assignmentInfo.assessments?.length > 0) {
                // For assessments, set the isNew value based on assessment statuses
                const allStarted = alp.assignmentInfo.assessments.filter(
                  (assessment) => assessment.status !== "NotStarted"
                );

                alp.isNew = alp.isNew && allStarted.length === 0;
              }
              return alp;
            })
          );
          setLoaded(true);
        }
      };
      fetchData();
    }
  }, [user, contentTypeForAlps]);

  const [currentMenuTab, setCurrentMenuTab] = React.useState("all");
  const [selectedAlp, setSelectedAlp] = React.useState(alps);
  const [filteredSubject, setFilteredSubject] = React.useState<JSX.Element>();
  let licensedSubjects = alps.map((alp) => alp.subject);
  licensedSubjects = Array.from(new Set(licensedSubjects));
  const selIndex = licensedSubjects.indexOf("SEL");
  const { goHome } = useHomePage();

  if (selIndex !== -1) {
    licensedSubjects[selIndex] = "Purpose";
  }

  const allMenuTabSubjects = Object.keys(Subjects)
    .filter((key) => licensedSubjects.includes(key))
    .map((item) => ({
      title: item,
      eventKey: item === "Purpose" ? "sel" : item.toLowerCase(),
      content: filteredSubject,
      className: "myassignment_tabs_container-content abld-tiles-overflow"
    }));

  // For small screens, create dropdown options for tab headers
  const tabDropdownOptions = [
    { label: "All Subjects", value: "all" },
    ...allMenuTabSubjects.map((tab) => ({ label: tab.title, value: tab.eventKey }))
  ];
  const filterSubject = (subjectId: string) => {
    if (subjectId !== Subjects.All) {
      setSelectedAlp(alps.filter((item) => item.subject.toLowerCase() === subjectId));
      setCurrentMenuTab(subjectId);
      return;
    }
    setSelectedAlp(alps);
    setCurrentMenuTab(subjectId);
  };

  alps.sort((a, b) => {
    if ((a.isNew && b.isNew) || (!a.isNew && !b.isNew && !a.isCompleted && !b.isCompleted)) {
      if (utcDateHandler(a.assignmentInfo.dueDate) < utcDateHandler(b.assignmentInfo.dueDate)) {
        return -1;
      }
      if (utcDateHandler(a.assignmentInfo.dueDate) === utcDateHandler(b.assignmentInfo.dueDate)) {
        if (a.assignmentInfo.assignedDate < b.assignmentInfo.assignedDate) {
          return -1;
        }
      }
    }
    return Number(b.isNew) - Number(a.isNew);
  });

  useEffect(() => {
    setSelectedAlp(alps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alps]);

  const getTile = (alp: IAlp) => <MyAssignmentsTile key={alp.id} alp={alp} subjectName={alp.subject} />;

  const activeAssignments = () => (
    <div className="abld-tiles">{selectedAlp.map((alp: IAlp) => !alp.isCompleted && getTile(alp))}</div>
  );

  useEffect(() => {
    setFilteredSubject(activeAssignments());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAlp]);

  return (
    <div className={classMap("my-assignments-menu-container w-100 abld-h-100", String(theme).replace(/_/g, "-"))}>
      {!loaded && spinner}
      {loaded && (alps.length === 0 ? <NoAssignmentPage /> : "")}
      {loaded && alps.length > 0 && (
        isSmallScreen ? (
          <>
            <SmallScreenPanel onBackClick={goHome} >
              <div className="myassignment-tabs-dropdown-center">
                <Dropdown onSelect={filterSubject}>
                  <Dropdown.Toggle
                    className="myassignment-tabs-dropdown"
                    id="myassignment-tabs-dropdown-toggle"
                    aria-label="Select assignment subject"
                  >
                    {tabDropdownOptions.find((opt) => opt.value === currentMenuTab)?.label ?? "All Subjects"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {tabDropdownOptions.map((opt) => (
                      <Dropdown.Item key={opt.value} eventKey={opt.value} active={currentMenuTab === opt.value}>
                        {opt.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </SmallScreenPanel>
            <div className="myassignment_tabs_container tab-content myassignment_tabs_container-content abld-tiles-overflow">
              {filteredSubject}
            </div>
          </>
        ) : (
          <>
            <EdgeBackButton
              onBackClick={() => goHome()}
              classNames={`abld-assignment-back-button${isKto5 ? "-k-2" : ""}`}
            />
            <UxTabs
              tabsId="myassignment-tabs"
              className="myassignment_tabs_container"
              activeKey={currentMenuTab}
              onTabSelect={(subjectId: string) => filterSubject(subjectId)}
              items={[
                {
                  title: "All",
                  eventKey: "all",
                  content: filteredSubject,
                  className: "myassignment_tabs_container-content abld-tiles-overflow"
                }
              ].concat(allMenuTabSubjects)}
            />
          </>
        )
      )}
    </div>
  );
}

export default MyAssignmentsMenu;
